import {useDblIntl} from "@/pages/datalab/utils/utils";
import React, {useEffect, useRef, useState} from "react";
import {Button, Card, Layout, List, Modal, notification} from "tea-component";
import {connect, history} from "umi";
import TableForm from '@/pages/datalab/pages/Number/DataManagement/TableEdit/FieldsTableForm';
import InfoForm from "@/pages/datalab/pages/Number/DataManagement/TableEdit/InfoForm";
import {buriedPoint} from "@home/utils/buriedPoint";
import './index.scss';
import { getTableInfoList, updateTableField } from '@dbl/services/dataSource';
import { FieldType } from '@dbl/core/dataSource';
import {useRequest} from "ahooks";
import AbnormalPage from '@/pages/datalab/components/AbnormalPage';

const { Content } = Layout;

const FormLabel = {
  'customize_name': 'name',
  'label': 'level_one',
  'sub_label': 'level_two',
  'cycle': 'cycle',
};

function Index(props: any) {
  const { gameCode } = props;
  const tableId = props?.location?.query?.tableId;
  const tableName = props?.location?.query?.tableName;
  const [saveLoading, setSaveLoading] = useState(false);
  const { dblFormatMessage: dataFormatMessage, isEn, commonFormatMessage } = useDblIntl("data_table");
  const infoRef = useRef({}); // 用于比较 用户是否修改了
  // 字段的信息
  const fieldsRef = useRef({});
  // 保存是否成功
  const saveSuccessRef = useRef(false);
  
  // 发起表格详情的请求
  const { data, loading, error } = useRequest(() => getTableInfoList([tableId], gameCode));
  
  useEffect(() => {
    if (!tableId) {
      history.replace(`/datalab/${gameCode}/DataManagement/SheetManagement`);
      return;
    }
    
    buriedPoint({
      pageId: '601401',
      pageName: tableName,
      pagePath: '/DataManagement/SheetManagement/Edit',
      extInfo: `{"pageName": "${tableName}", "pageNameCn": "", "id": "${tableId}"}`,
      uid: gameCode,
      typeId: 'datalab_pc_v2',
      menuId: '0'
    });
    return () => {
      sessionStorage.removeItem('dbl-table-info');
    };
  }, [gameCode]);
  
  useEffect(() => {
    
    // 前端路由进入其他页面时 是否弹出提示
    const unblock = history.block((location) => {
      // 采用block方法可以只在离开页面时判断是否需要弹窗提示, 而不需要根据用户输入动态随时更新是否弹出提示的状态, 所以没有使用CustomPrompt组件.
      if (calculateIsChange().isChange && (!saveSuccessRef.current)) {
        // 有更改 弹出提示
        Modal.confirm({
          message: dataFormatMessage('edit_leave'),
          description: commonFormatMessage('leave_message', {name: ''}),
          okText: commonFormatMessage('leave'),
          cancelText: commonFormatMessage('cancel'),
          onOk: () => {
            unblock();
            history.replace(`${location.pathname}${location.search}`);
          }
        });
        return false;
      }
      return true;
    });
    
    // 页面刷新时 弹出系统提示
    const listener = (event) => {
      event.preventDefault();
      event.returnValue = calculateIsChange().isChange;
    };
    window.addEventListener('beforeunload', listener);
    
    return () => {
      unblock();
      window.removeEventListener('beforeunload', listener);
    };
    
  }, []);
  
  // 计算用户更改的是否校验成功
  const calculateValidation = () => {
    const infoValidations = infoRef.current.getValidations();
    const fieldValidations = fieldsRef.current?.getValidations();
    
    const validations = [ ...infoValidations, ...fieldValidations];
    return {
      validations,
      isValidationSuccess: validations.length === 0
    };
  };
  
  const calculateIsChange = () => {
    // 表信息是否更改
    const tableInfo = infoRef.current.getNewInfo?.() || {};
    const isInfoChange = JSON.stringify(tableInfo) !== '{}';
    // 字段是否有更改
    const fieldInfos = fieldsRef.current.getNewFields?.();
    const isFieldChange = fieldInfos?.length > 0;
    return {
      isChange: isInfoChange || isFieldChange,
      tableInfo,
      fieldInfos
    };
  };
  
  // 保存按钮
  const saveBtn = () => {
    try {
      const {validations, isValidationSuccess} = calculateValidation();
      if (isValidationSuccess) {
        setSaveLoading(true);
        const {tableInfo, fieldInfos} = calculateIsChange();
        fieldInfos.forEach(info => {
          // 需求: 类型更改为 小数或百分比类型时, 小数位数都传10 (不考虑之前的小数位数是多少)
          (info.column_type === FieldType.Float || info.column_type === FieldType.Percent) && (info.accuracy = 10);
        });
        const params = {
          table_id: props?.location?.query?.tableId,
          ...tableInfo,
          columns: fieldInfos
        };
        updateTableField(params).then(() => {
          notification.success({
            description: commonFormatMessage('save_success'),
          });
          saveSuccessRef.current = true;
          history.goBack();
        }).catch(e => {
          console.error('编辑数据表保存错误: ', e);
          notification.error({
            description: commonFormatMessage('save_fail'),
          });
        }).finally(() => {
          setSaveLoading(false);
        });
    
      } else {
        Modal.alert({
          message: commonFormatMessage('save_fail'),
          description: (<List type="bullet">
            {
              validations.map((item) => (<List.Item key={item.name}>{item.isTableInfo ? dataFormatMessage(FormLabel[item.name]) : item.name}: {item[isEn ? 'messageEn' : 'message']}</List.Item>))
            }
          </List>),
          buttons: [<Button key={1} type="primary">{commonFormatMessage('confirm')}</Button>]
        });
    
      }
    } catch (e) {
      console.error(e);
    }
    
  };
  
  return <Content className={'dbl-table-edit'}>
    <Content.Header
      showBackButton
      title={dataFormatMessage('edit_title')}
      onBackButtonClick={() => { history.goBack() }}
    />
    <Content.Body full>
      <Card>
        <Card.Body>
          <AbnormalPage status={loading ? 'loading' : (error ? 'error' : 'normal')}>
          
            <InfoForm ref={infoRef} dataFormatMessage={dataFormatMessage} isEn={isEn} commonFormatMessage={commonFormatMessage} tableInfo={data?.[0] || {}}/>
            <TableForm ref={fieldsRef} tableId={tableId} dataFormatMessage={dataFormatMessage} isEn={isEn} commonFormatMessage={commonFormatMessage} loading={loading} error={error} dataList={data?.[0]?.column_list}/>
          </AbnormalPage>
        
        </Card.Body>
        
      </Card>
    </Content.Body>
    <Content.Footer>
      <Button loading={saveLoading} type='primary' onClick={saveBtn}>{commonFormatMessage('save')}</Button>
      <Button onClick={() => {history.goBack()}}>{commonFormatMessage('cancel')}</Button>
    </Content.Footer>
  </Content>;
}

export default connect((state) => {
  const { gameCode } = state.datalab;
  return {
    gameCode,
  };
})(Index);